import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

// Keep buttons (and icon-only buttons like carousel arrows) visually centered.
const buttonVariants = cva("inline-flex items-center justify-center gap-2 text-center", {
	variants: {
		variant: {
			default: `
				web-button bg-blue-700 text-white p-4 rounded-[36px] hover:bg-[#2038bf] disabled:bg-gray-500
				border-[1.5px] border-blue-500-10
				shadow-[0px_4px_0.9px_0px_#004096,inset_0px_-11px_14.7px_0px_rgba(144,_231,_255,_0.4)]
				hover:shadow-[0px_2px_0.9px_0px_#004096,inset_0px_11px_14.7px_0px_rgba(144,_231,_255,_0.4)]
				disabled:shadow-none
				`,
			secondary: `
				web-button text-black bg-[rgba(46,_127,_219,_0.1)] hover:bg-[rgba(46,_127,_219,_0.2)] rounded-[32px]
				border-1 p-4
				border-[rgba(46,_127,_219,_0.2)]
				disabled:border-[1.5px] disabled:border-[rgba(129,_136,_146,_0.2)] disabled:text-gray-500
					`,
			white:
				"bg-white py-4 hover:bg-white rounded-[36px] shadow-[0px_4px_0.9px_0px_#004096] text-blue-700 web-button w-full",

			destructive:
				"bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
			outline:
				"border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",

			ghost:
				"hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
			link: "text-primary underline-offset-4 hover:underline",
		},
		size: {
			default: "h-14 cursor-pointer",
			// sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
			// lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
			icon: "size-9 p-0",
		},
	},
	defaultVariants: {
		variant: "default",
		size: "default",
	},
});

function Button({
	className,
	variant,
	size,
	asChild = false,
	...props
}: React.ComponentProps<"button"> &
	VariantProps<typeof buttonVariants> & {
		asChild?: boolean;
	}) {
	const Comp = asChild ? Slot : "button";

	return (
		<Comp
			data-slot="button"
			className={cn(buttonVariants({ variant, size, className }))}
			{...props}
		/>
	);
}

export { Button, buttonVariants };
